import { useEffect, useState } from "react";

export type RaceClockPhase =
  "scheduled" | "betting" | "starting" | "running" | "official";

export function useRaceClock({
  gatesOpenAt,
  locksAt,
  serverTime,
  status,
  playbackAvailable,
  raceDurationMs,
}: {
  gatesOpenAt: string;
  locksAt?: string | null;
  serverTime?: string | null;
  status: string;
  playbackAvailable: boolean;
  raceDurationMs: number;
}) {
  const [clock, setClock] = useState(() => {
    const clientNow = Date.now();
    return {
      clientNow,
      serverOffsetMs: serverTime ? Date.parse(serverTime) - clientNow : 0,
    };
  });

  useEffect(() => {
    const interval = window.setInterval(() => {
      setClock((current) => ({
        ...current,
        clientNow: Date.now(),
      }));
    }, 100);
    return () => window.clearInterval(interval);
  }, []);

  useEffect(() => {
    const update = window.setTimeout(() => {
      const clientNow = Date.now();
      setClock({
        clientNow,
        serverOffsetMs: serverTime ? Date.parse(serverTime) - clientNow : 0,
      });
    }, 0);
    return () => window.clearTimeout(update);
  }, [serverTime]);

  const now = clock.clientNow + clock.serverOffsetMs;
  const gateTime = Date.parse(gatesOpenAt);
  const lockTime = locksAt ? Date.parse(locksAt) : gateTime;
  const countdownMs = Math.max(0, gateTime - now);
  const bettingCountdownMs = Math.max(0, lockTime - now);
  const elapsedMs = Math.max(0, now - gateTime);
  let phase: RaceClockPhase;
  if (status === "official") {
    phase = "official";
  } else if (playbackAvailable) {
    phase = "running";
  } else if (
    status === "locked" ||
    status === "running" ||
    countdownMs <= 0 ||
    (status === "betting" && bettingCountdownMs <= 0)
  ) {
    phase = "starting";
  } else if (status === "betting") {
    phase = "betting";
  } else {
    phase = "scheduled";
  }

  return {
    nowMs: now,
    serverOffsetMs: clock.serverOffsetMs,
    countdownMs,
    countdownLabel: formatCountdown(countdownMs),
    bettingCountdownMs,
    bettingCountdownLabel: formatCountdown(bettingCountdownMs),
    elapsedMs,
    progress:
      phase === "official"
        ? 1
        : Math.max(0, Math.min(1, elapsedMs / Math.max(1, raceDurationMs))),
    phase,
  };
}

export function formatCountdown(milliseconds: number) {
  const totalSeconds = Math.max(0, Math.ceil(milliseconds / 1000));
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${seconds.toString().padStart(2, "0")}`;
}

/** Fullscreen HUD capsule copy (PLAN §"Racing V2 interface" → "Fullscreen
 * countdown"): the same clock milliseconds the page titlebar formats,
 * rendered as the phase capsule's text. Betting counts to locksAt (the
 * bettor's number), the locked beat counts the last five seconds to the
 * gates (the donor casino's GATES IN call), a not-yet-open race counts to
 * the gates. During the official intermission (the result hold and any
 * waiting-for-schedule stretch) the capsule answers "when does the next
 * race start": NEXT RACE IN counts to the next race's published
 * scheduled_at — public races-list data, nothing the poller couldn't
 * know — and falls back to WAITING FOR NEXT RACE across schedule gaps.
 * Running (and a non-advancing official result, e.g. an archive replay)
 * returns null — a stale countdown is a display bug. Inputs are clamped
 * here because the clock value may slew under server-offset corrections;
 * the non-breaking space keeps the timer from wrapping away from its
 * separator on portrait phones. */
export function overlayCountdownText({
  phase,
  bettingOpen,
  countdownMs,
  bettingCountdownMs,
  intermission = false,
  nextRaceStartMs = null,
}: {
  phase: RaceClockPhase;
  bettingOpen: boolean;
  countdownMs: number;
  bettingCountdownMs: number;
  /** The continuous loop is advancing: the result hold is counting down
   * or the swap is gated on the next racecard. Archive replays and
   * pending settlements never set this. */
  intermission?: boolean;
  /** Milliseconds until the next race's scheduled gates, when the public
   * races list knows the next round; null across schedule gaps. */
  nextRaceStartMs?: number | null;
}): string | null {
  if (phase === "running") return null;
  if (phase === "official") {
    if (!intermission) return null;
    if (nextRaceStartMs === null) return "WAITING FOR NEXT RACE";
    return `NEXT RACE IN ${formatCountdown(clampMs(nextRaceStartMs))}`;
  }
  if (phase === "betting" && bettingOpen) {
    return `BETTING OPEN ·\u00a0${formatCountdown(clampMs(bettingCountdownMs))}`;
  }
  if (phase === "scheduled") {
    return `STARTS IN ${formatCountdown(clampMs(countdownMs))}`;
  }
  const gatesMs = clampMs(countdownMs);
  const gatesSeconds = Math.ceil(gatesMs / 1000);
  if (gatesSeconds > 0 && gatesSeconds <= 5) {
    return `GATES IN ${formatCountdown(gatesMs)}`;
  }
  return null;
}

function clampMs(milliseconds: number) {
  return Number.isFinite(milliseconds) ? Math.max(0, milliseconds) : 0;
}
